import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaFileAlt,
  FaRegCalendarAlt,
  FaTags,
  FaTicketAlt,
} from "react-icons/fa";
import { GoArrowLeft } from "react-icons/go";
import moment from "moment-jalali";

import service from "../../../core/service";
import routes from "../../../core/routes";
import { calculateNext, calculatePrev } from "../../../core/utils";
import { Layout, LoadingSpinner } from "../../components";
import { persianNumber } from "../../components/datePicker/utils/persian";
import { ticketDisplaySubject } from "./ticketSubject";

moment.loadPersian();

interface CalendarTicket {
  date: string;
  subject: string;
  registrar: string;
  ticket_id: number;
  ticket_subject?: string;
  lead_state_name?: string;
  deal_state_name?: string;
  deal_stage_name?: string;
}

// English lead-state slug → Persian title + on-brand colors
const LEAD_STATE_ORDER = ["active", "follow up", "completion", "unwillingness", "wall"];

const LEAD_STATES: Record<string, { title: string; color: string; bg: string }> = {
  active: { title: "فعال", color: "#009595", bg: "#e2f2f0" },
  "follow up": { title: "پیگیری", color: "#ca9602", bg: "#f8eed7" },
  completion: { title: "تکمیل شده", color: "#0c1a58", bg: "#e8ecf0" },
  unwillingness: { title: "عدم تمایل", color: "#f45632", bg: "#fbe6e0" },
  wall: { title: "دیوار", color: "#525151", bg: "#efeded" },
};

// fallback color/style for any state slug not present in LEAD_STATES;
// the title shown is the raw backend value so nothing ever appears unnamed
const UNKNOWN_STATE_STYLE = { color: "#525151", bg: "#efeded" };

function CalendarDayPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { year, month, day } = useParams();
  const dateKey = `${year}/${month}/${day}`;
  const boardRef = useRef<HTMLDivElement>(null);

  // horizontal overflow detection — device-independent: the board is draggable only
  // when columns actually overflow (a wide laptop fits them, a macbook may not)
  const [canDrag, setCanDrag] = useState(false);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const dragState = useRef({ startX: 0, startLeft: 0, moved: false, captured: false });

  // no wheel hijack: the mouse roller always scrolls the page vertically;
  // horizontal movement happens by dragging the board (or trackpad/scrollbar)
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return; // touch keeps native scrolling
    const board = boardRef.current;
    if (!board || board.scrollWidth <= board.clientWidth + 1) return;
    dragState.current = {
      startX: e.clientX,
      startLeft: board.scrollLeft,
      moved: false,
      captured: false,
    };
    draggingRef.current = true;
    // NOTE: no pointer capture and no dragging state here — capturing on press
    // would retarget the resulting click to the board and break the card onClick;
    // the dragging state engages only once the pointer really starts moving
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const board = boardRef.current;
    if (!board) return;
    const st = dragState.current;
    const dx = e.clientX - st.startX;
    if (Math.abs(dx) > 4) {
      st.moved = true;
      setDragging(true);
      // capture only once a real drag starts, so a plain click still lands on the card
      if (!st.captured) {
        st.captured = true;
        try {
          board.setPointerCapture(e.pointerId);
        } catch {
          /* capture unavailable */
        }
      }
    }
    board.scrollLeft = st.startLeft - dx;
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>, cancel: boolean) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    // pointercancel means no click follows — clear the flag so the next
    // normal click is not suppressed
    if (cancel) dragState.current.moved = false;
    if (dragState.current.captured) {
      try {
        boardRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already released */
      }
      dragState.current.captured = false;
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => endDrag(e, false);
  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => endDrag(e, true);

  // a drag that started on a kanban-card must not open the ticket on release
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.moved) {
      dragState.current.moved = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // tickets passed via router state — the calendar request is NOT sent again
  const stateTickets = (location.state as any)?.tickets as CalendarTicket[] | undefined;
  const [tickets, setTickets] = useState<CalendarTicket[]>(stateTickets || []);
  const [loading, setLoading] = useState(!stateTickets);

  // horizontal overflow detection — device-independent: the board is draggable only
  // when columns actually overflow (a wide laptop fits them, a macbook may not)
  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const update = () => setCanDrag(board.scrollWidth > board.clientWidth + 1);
    update();
    window.addEventListener("resize", update);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(board);
    }
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    if (stateTickets) return; // already have data — never refetch
    let cancelled = false;
    (async () => {
      try {
        const m = moment(`${year}/${month}/15`, "jYYYY/jM/jDD");
        const response = await service.Calender.list(calculatePrev(m), calculateNext(m));
        if (cancelled) return;
        const all = response?.data?.data?.list || [];
        setTickets(all.filter((t: CalendarTicket) => t.date === dateKey));
      } catch {
        if (!cancelled) setTickets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // kanban columns grouped by lead_state_name, each sub-grouped by registrar
  const columns = useMemo(() => {
    const stateOrderMap = new Map<string, number>();
    LEAD_STATE_ORDER.forEach((s, i) => stateOrderMap.set(s, i));

    const map: Record<string, Record<string, CalendarTicket[]>> = {};
    const order: string[] = [];

    tickets.forEach((t) => {
      const state = t.lead_state_name || "";
      if (!map[state]) {
        map[state] = {};
        order.push(state);
      }
      const registrar = t.registrar || "—";
      (map[state][registrar] = map[state][registrar] || []).push(t);
    });

    // known states first (in their defined order), unknown slugs after
    order.sort((a, b) => {
      const ai = stateOrderMap.has(a) ? stateOrderMap.get(a)! : Number.MAX_SAFE_INTEGER;
      const bi = stateOrderMap.has(b) ? stateOrderMap.get(b)! : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });

    return order.map((state) => ({
      state,
      meta: LEAD_STATES[state] || {
        title: state || "نامشخص",
        ...UNKNOWN_STATE_STYLE,
      },
      registrars: map[state],
      total: Object.values(map[state]).reduce((n, arr) => n + arr.length, 0),
    }));
  }, [tickets]);

  const dateTitle = moment(dateKey, "jYYYY/jMM/jDD").format("dddd");

  return (
    <Layout title="تیکت‌های روز">
      <Helmet>
        <title>تیکت‌های روز | سامانه مشتریان</title>
      </Helmet>
      <div className="calendar-day-page">
        <div className="calendar-day-header">
          <div className="calendar-day-title">
            <FaRegCalendarAlt />
            <span>
              {dateTitle} {persianNumber(dateKey)}
            </span>
            <span className="calendar-day-count">{persianNumber(tickets.length)} تیکت</span>
          </div>
          <button
            type="button"
            className="calendar-back-btn"
            onClick={() => navigate(routes.CALENDEER)}
          >
            بازگشت به تقویم
            <GoArrowLeft />
          </button>
        </div>

        {loading ? (
          <div className="calendar-loading">
            <LoadingSpinner />
          </div>
        ) : columns.length === 0 ? (
          <div className="calendar-empty">
            <FaTicketAlt />
            <span>در این روز تیکتی ثبت نشده است</span>
          </div>
        ) : (
          <div
            ref={boardRef}
            className={`kanban-board${canDrag ? " is-draggable" : ""}${
              dragging ? " is-dragging" : ""
            }`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onClickCapture={onClickCapture}
          >
            {columns.map(({ state, meta, registrars, total }) => (
              <div
                key={state}
                className="kanban-column"
                style={
                  {
                    "--lead-color": meta.color,
                    "--lead-bg": meta.bg,
                  } as React.CSSProperties
                }
              >
                <div className="kanban-column-header">
                  <span className="kanban-column-dot" />
                  <span className="kanban-name">{meta.title}</span>
                  <span className="kanban-count">{persianNumber(total)}</span>
                </div>
                <div className="kanban-column-body">
                  {Object.entries(registrars).map(([registrar, items]) => (
                    <div key={registrar} className="kanban-registrar-group">
                      <div className="kanban-registrar-header">
                        <span className="kanban-avatar">{registrar?.charAt(0) || "؟"}</span>
                        <span className="kanban-registrar-name">{registrar}</span>
                        <span className="kanban-registrar-count">
                          {persianNumber(items.length)}
                        </span>
                      </div>
                      <div className="kanban-registrar-cards">
                        {items.map((t) => (
                          <div
                            key={t.ticket_id}
                            className="kanban-card"
                            onClick={() =>
                              window.open(
                                routes.DETAILSTICKET.replace(":id", String(t.ticket_id)),
                                "_blank"
                              )
                            }
                          >
                            <span className="kanban-card-subject">
                              <FaFileAlt className="kanban-card-subject-icon" />
                              {ticketDisplaySubject(t)}
                            </span>
                            <div className="kanban-card-footer">
                              {t.deal_stage_name && (
                                <span className="kanban-card-badge">
                                  <FaTags />
                                  {t.deal_stage_name}
                                </span>
                              )}
                              <span className="kanban-card-id">
                                <FaTicketAlt /> #{persianNumber(t.ticket_id)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CalendarDayPage;
